import React, { useEffect } from 'react';
import { X, AlertTriangle, Package, MapPin, CheckCircle, Sparkles, Check, Truck } from 'lucide-react';
import { NewCombo } from '../data/newCombos';
import { WA_NUMBER } from '../data/products';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
interface NewComboModalProps {
  combo: NewCombo | null;
  onClose: () => void;
}
export function NewComboModal({
  combo,
  onClose
}: NewComboModalProps) {
  useEffect(() => {
    document.body.style.overflow = combo ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [combo]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  if (!combo) return null;
  const waLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(combo.waMessage)}`;
  return <div className="fixed inset-0 bg-black/90 z-[9999] overflow-y-auto p-[30px_20px] backdrop-blur-sm flex items-start justify-center" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-248" data-mp="1dvagkph3kxat">
      <div className="bg-dark-600 border-2 border-alphaGold/50 rounded-xl max-w-[780px] w-full overflow-hidden relative m-auto animate-modal-in" data-id="element-249" data-mp="1lkyjl731yx42">
        <button onClick={onClose} className="absolute top-[15px] right-[20px] bg-transparent border-none text-white cursor-pointer z-20 leading-none transition-colors duration-200 hover:text-alphaGold" aria-label="Cerrar" data-id="element-250" data-mp="1go83wkv5u3j6">
          <X size={32} data-id="element-251" data-mp="24jkjwjqqwk6x" />
        </button>

        <div className={`w-full flex items-center justify-center overflow-hidden p-6 ${combo.imageOnLightCard ? 'bg-white' : 'bg-dark-800'}`} data-id="element-252" data-mp="24i9gwm54jggv">
          <img src={combo.image} alt={combo.imageAlt} className={`w-full h-auto block ${combo.imageOnLightCard ? '' : 'drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]'}`} data-id="element-253" data-mp="2x1osixyv231d" />
        </div>

        <div className="p-[35px]" data-id="element-254" data-mp="qd6c1r57o20n">
          <div className="inline-flex items-center gap-1.5 bg-alphaGold text-dark-900 text-[0.75rem] font-extrabold py-1 px-3.5 rounded-sm tracking-[2px] uppercase mb-[15px]" data-id="element-255" data-mp="vbhj5u44fyf6">
            <Sparkles className="w-3.5 h-3.5" data-id="element-256" data-mp="gkh2x2x4v08h" /> Nuevo Ingreso
          </div>

          <h2 className="text-[1.6rem] font-black uppercase tracking-[2px] text-white mb-[15px]" data-id="element-257" data-mp="lk3hbfop6miv">
            {combo.name}
          </h2>

          <div className="bg-[#1e1e1e] rounded-lg p-4 mb-5" data-id="element-258" data-mp="bnibis4ut7dz">
            <h4 className="text-alphaGold text-[0.8rem] uppercase tracking-[2px] mb-3 font-bold flex items-center gap-2" data-id="element-259" data-mp="bp1oa8rax0b0">
              <Package className="w-4 h-4" data-id="element-260" data-mp="3tvg309xe9eag" /> Este combo incluye
            </h4>
            <ul className="space-y-2" data-id="element-261" data-mp="2n281io42094">
              {combo.includes.map(item => <li key={item} className="flex items-start gap-2 text-[#ddd] text-[0.9rem] font-medium" data-id="element-262" data-mp="1s29teb4diw91">
                  <Check className="w-4 h-4 text-alphaGold shrink-0 mt-0.5" data-id="element-263" data-mp="3hiccnl0o3eq0" />
                  <span data-id="element-264" data-mp="3cjsr3lpzk4y6">{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="bg-alphaGold/10 border-l-4 border-l-alphaGold p-[18px_20px] mb-5 rounded-r" data-id="element-265" data-mp="3r8oyrho4rfy9">
            <h4 className="text-alphaGold text-[0.85rem] uppercase tracking-[2px] mb-2 font-bold flex items-center gap-2" data-id="element-266" data-mp="3mi8izqwu7i8q">
              <AlertTriangle className="w-4 h-4" data-id="element-267" data-mp="43684i3qaq4s" /> El Problema que Resuelve
            </h4>
            <p className="text-[#ccc] text-[0.92rem] leading-[1.7]" data-id="element-268" data-mp="3vb207tvugrp8">
              {combo.pain}
            </p>
          </div>

          <p className="text-[#aaa] leading-[1.8] text-[0.97rem] mb-[25px]" data-id="element-269" data-mp="e1n4xvpclyyr">
            {combo.description}
          </p>

          <p className="text-[0.85rem] uppercase tracking-[2px] text-[#666] mb-3 font-bold flex items-center gap-2" data-id="element-270" data-mp="90zepc8y1m5l">
            <CheckCircle className="w-4 h-4 text-alphaGold" data-id="element-271" data-mp="2auf8gi0bvh4s" /> Beneficios Clave
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-[25px]" data-id="element-272" data-mp="2awrvh9ky0hop">
            {combo.benefits.map(benefit => <div key={benefit} className="flex items-start gap-2.5 bg-[#1e1e1e] p-3 rounded" data-id="element-273" data-mp="16w209q8ygbg8">
                <span className="text-alphaGold font-black text-[1rem] flex-shrink-0 mt-px" data-id="element-274" data-mp="1bwfzqjwhqd2n">
                  ✓
                </span>
                <span className="text-[#ccc] text-[0.85rem] leading-[1.5]" data-id="element-275" data-mp="j6z8vqoir48j">
                  {benefit}
                </span>
              </div>)}
          </div>

          <div className="flex items-center gap-4 mb-[25px] flex-wrap" data-id="element-276" data-mp="o3zkeh450z71">
            <span className="text-[2.5rem] font-black text-alphaGold" data-id="element-277" data-mp="s649d2jn57nc">
              ${combo.price.toLocaleString('es-UY')}
            </span>
            {combo.originalPrice && <span className="text-[1.4rem] font-bold text-[#666] line-through" data-id="element-278" data-mp="fgbbzd9kidw">
                ${combo.originalPrice.toLocaleString('es-UY')}
              </span>}
            {combo.freeShipping && <span className="inline-flex items-center gap-2 bg-alphaGold/15 border border-alphaGold/40 text-alphaGold text-[0.85rem] font-extrabold py-1.5 px-4 rounded-full" data-id="element-279" data-mp="3t3g163xiyxqu">
                <Truck className="w-4 h-4" data-id="element-280" data-mp="3k28g4nk0rtep" /> Envío gratis en Montevideo
              </span>}
          </div>

          <div className="bg-alphaGold/10 border border-alphaGold/25 rounded p-[14px_18px] mb-5 text-[0.88rem] text-alphaGold font-semibold text-center flex items-center justify-center gap-2" data-id="element-281" data-mp="3f3oqac2b1xh2">
            <AlertTriangle className="w-4 h-4" data-id="element-282" data-mp="35dlrtwcezrbc" /> Stock limitado · Nuevo ingreso
            por tiempo limitado
          </div>

          <div className="bg-dark-500 border border-dark-300 rounded p-[15px_18px] mb-5 text-[0.85rem] text-[#999] space-y-2" data-id="element-283" data-mp="30d7ogrlrd5jn">
            <div className="flex items-start gap-2" data-id="element-284" data-mp="2reckzna3m9k1">
              <Truck className="w-4 h-4 mt-0.5 text-alphaGold flex-shrink-0" data-id="element-285" data-mp="2rdufe01jqizg" />
              <span data-id="element-286" data-mp="2mguahsgfnm9x">
                {combo.freeShipping ? <>
                    <strong className="text-white" data-id="element-287" data-mp="2cqvr2dxzk4oy">Envío gratis</strong> dentro
                    de Montevideo
                  </> : <>
                    <strong className="text-white" data-id="element-288" data-mp="22tzik8837ike">Envío a domicilio:</strong>{' '}
                    desde $190 pesos uruguayos, varía según tu zona
                  </>}
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-289" data-mp="2vjg6otoocdat">
              <MapPin className="w-4 h-4 mt-0.5 text-alphaGold flex-shrink-0" data-id="element-290" data-mp="30hhzi6bwjqzh" />
              <span data-id="element-291" data-mp="30jkmsilo760i">
                <strong className="text-white" data-id="element-292" data-mp="2lslg5d4earjk">Pick Up:</strong> Malvín,
                Montevideo
              </span>
            </div>
          </div>

          <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-whatsapp text-white text-[1.05rem] font-extrabold py-[18px] px-[30px] rounded uppercase tracking-[1px] transition-all duration-300 w-full shadow-[0_6px_25px_rgba(37,211,102,0.3)] hover:bg-whatsappDark hover:scale-[1.02]" data-id="element-293" data-mp="2qqdiz26kvy2j">
            <WhatsAppIcon className="w-6 h-6" data-id="element-294" data-mp="12te4pzz4p72s" />
            Quiero este combo — Pedir por WhatsApp
          </a>
        </div>
      </div>
    </div>;
}